import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Save, Trash2, GripVertical, Clock, Power, PowerOff } from "lucide-react";

// Marketing funnel registry — keep in sync with backend/funnels.py
const FUNNEL_OPTIONS = [
  { value: "all",         label: "كل الفنلات" },
  { value: "flex",        label: "المرونة (/flex)" },
  { value: "homeworkout", label: "التمارين المنزلية (/homeworkout)" },
];
const FUNNEL_LABELS = FUNNEL_OPTIONS.reduce((a, o) => (a[o.value] = o.label, a), {});

// Compound-trigger helpers: `lead_no_purchase:flex` <-> { base: 'lead_no_purchase', funnel: 'flex' }
const splitTrigger = (t = "") => {
  const [base, funnel] = String(t).split(":");
  return { base: base || "lead_no_purchase", funnel: funnel || "all" };
};
const joinTrigger = (base, funnel) =>
  !funnel || funnel === "all" ? base : `${base}:${funnel}`;

// Group templates by their `folder` field so the picker shows a folder heading
// with its templates nested under it. Unfoldered items land in a
// "غير مصنّف" bucket at the bottom. Folder order = alphabetical (case-insensitive),
// templates inside each folder = alphabetical by display name.
function groupTemplatesByFolder(templates) {
  const UNCATEGORIZED = "غير مصنّف";
  const buckets = {};
  for (const t of templates) {
    const folder = (t.folder || "").trim() || UNCATEGORIZED;
    (buckets[folder] = buckets[folder] || []).push(t);
  }
  const folderNames = Object.keys(buckets).sort((a, b) => {
    // Push "غير مصنّف" to the end so real folders come first.
    if (a === UNCATEGORIZED) return 1;
    if (b === UNCATEGORIZED) return -1;
    return a.localeCompare(b, "ar");
  });
  return folderNames.map((f) => ({
    folder: f,
    templates: buckets[f].sort((a, b) => {
      const an = (a.name || a.key || "").toLowerCase();
      const bn = (b.name || b.key || "").toLowerCase();
      return an.localeCompare(bn, "ar");
    }),
  }));
}

export default function AdminEmailSequences() {
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [s, t, av] = await Promise.all([
      api.get("/admin/emails/sequences"),
      api.get("/admin/emails/templates"),
      api.get("/admin/emails/available-variables"),
    ]);
    setSequences(s.data);
    setTemplates(t.data);
    setTriggers(av.data.trigger_types || []);
  };

  useEffect(() => { load(); }, []);

  const newSequence = () => {
    setEditing({ name: "تسلسل جديد", trigger: "lead_no_purchase", _funnel: "all", active: true, steps: [] });
  };

  const editSequence = (s) => {
    const clone = JSON.parse(JSON.stringify(s));
    const parts = splitTrigger(clone.trigger);
    clone.trigger = parts.base;
    clone._funnel = parts.funnel;
    setEditing(clone);
  };

  const cancelEdit = () => setEditing(null);

  const addStep = () => {
    setEditing((s) => ({ ...s, steps: [...s.steps, { template_key: templates[0]?.key || "", delay_minutes: 60, active: true }] }));
  };

  const removeStep = (i) => {
    setEditing((s) => ({ ...s, steps: s.steps.filter((_, idx) => idx !== i) }));
  };

  const updateStep = (i, patch) => {
    setEditing((s) => ({ ...s, steps: s.steps.map((st, idx) => idx === i ? { ...st, ...patch } : st) }));
  };

  const save = async () => {
    try {
      // Serialize funnel into the compound trigger before persisting.
      const payload = { ...editing, trigger: joinTrigger(editing.trigger, editing._funnel) };
      delete payload._funnel;
      if (editing.id) {
        await api.put(`/admin/emails/sequences/${editing.id}`, payload);
      } else {
        await api.post(`/admin/emails/sequences`, payload);
      }
      toast.success("تم الحفظ");
      setEditing(null);
      await load();
    } catch (e) { toast.error(extractError(e, "فشل الحفظ")); }
  };

  const remove = async (id) => {
    if (!confirm("حذف هذا التسلسل؟")) return;
    try {
      await api.delete(`/admin/emails/sequences/${id}`);
      toast.success("تم الحذف");
      await load();
    } catch (e) { toast.error(extractError(e, "فشل الحذف")); }
  };

  const toggle = async (id) => {
    try {
      await api.patch(`/admin/emails/sequences/${id}/toggle`);
      await load();
    } catch (e) { toast.error(extractError(e, "فشل التبديل")); }
  };

  const formatDelay = (m) => {
    if (m < 60) return `${m} دقيقة`;
    if (m < 1440) return `${Math.round(m / 60)} ساعة`;
    return `${Math.round(m / 1440)} يوم`;
  };

  return (
    <div className="space-y-6" data-testid="admin-emails-sequences">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">تسلسلات البريد</h1>
          <p className="text-foreground/60 mt-2 text-sm">أنشئ سلاسل رسائل تلقائية تعمل حسب أحداث معيّنة (شراء غير مكتمل، بعد التسجيل، إلخ).</p>
        </div>
        {!editing && <Button onClick={newSequence} data-testid="btn-new-seq"><Plus className="h-4 w-4 me-2" /> إضافة تسلسل</Button>}
      </div>

      {editing ? (
        <Card className="p-4 sm:p-6 space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <Label>اسم التسلسل</Label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="seq-name" />
            </div>
            <div>
              <Label>المُشغِّل (Trigger)</Label>
              <Select value={editing.trigger} onValueChange={(v) => setEditing({ ...editing, trigger: v })}>
                <SelectTrigger data-testid="seq-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {triggers.map((t) => <SelectItem key={t.key} value={t.key}>{t.desc}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>الفنل التسويقي</Label>
              <Select value={editing._funnel || "all"} onValueChange={(v) => setEditing({ ...editing, _funnel: v })}>
                <SelectTrigger data-testid="seq-funnel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FUNNEL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-foreground/50 mt-1">
                اختر "كل الفنلات" ليعمل التسلسل كافتراضي، أو حدّد فنل معيّن لاستهداف زوّار صفحة هبوط معيّنة فقط.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="seq-active" />
            <Label className="cursor-pointer">التسلسل مُفعّل ويعمل تلقائيًا</Label>
          </div>

          <div className="pt-2 border-t">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-display font-semibold">مراحل الرسائل</h3>
              <Button onClick={addStep} size="sm" variant="outline" data-testid="btn-add-step"><Plus className="h-4 w-4 me-1" /> رسالة</Button>
            </div>
            {editing.steps.length === 0 && <p className="text-sm text-foreground/50 py-4 text-center">لا توجد رسائل. اضغط إضافة رسالة.</p>}
            <div className="space-y-3">
              {editing.steps.map((step, i) => (
                <div key={i} className="flex flex-wrap items-end gap-2 border rounded-lg p-3 bg-black/[0.02]">
                  <div className="flex items-center gap-1 shrink-0"><GripVertical className="h-4 w-4 text-foreground/30" /><span className="text-xs">#{i + 1}</span></div>
                  <div className="flex-1 min-w-[180px]">
                    <Label className="text-xs">القالب</Label>
                    <Select value={step.template_key} onValueChange={(v) => updateStep(i, { template_key: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {groupTemplatesByFolder(templates).map((group) => (
                          <SelectGroup key={group.folder}>
                            <SelectLabel className="text-[11px] font-semibold text-orange-700 bg-orange-50/60 py-1.5">
                              📁 {group.folder}
                              <span className="text-[10px] text-foreground/50 me-1 ms-2">({group.templates.length})</span>
                            </SelectLabel>
                            {group.templates.map((t) => {
                              const displayName = t.name || t.key;
                              return (
                                <SelectItem key={t.key} value={t.key} data-testid={`step-tpl-${t.key}`}>
                                  <div className="flex flex-col items-start gap-0.5 py-0.5 ps-3">
                                    <span className="font-medium">{displayName}</span>
                                    {t.subject && t.subject !== displayName && (
                                      <span className="text-[10px] text-foreground/50 truncate max-w-[400px]">
                                        الموضوع: {t.subject}
                                      </span>
                                    )}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-[130px]">
                    <Label className="text-xs">تأخير بعد المرحلة السابقة (دقيقة)</Label>
                    <Input type="number" min={0} value={step.delay_minutes} onChange={(e) => updateStep(i, { delay_minutes: parseInt(e.target.value) || 0 })} className="h-9" />
                    <div className="text-[10px] text-foreground/50 mt-0.5 flex items-center gap-1"><Clock className="h-3 w-3" /> {formatDelay(step.delay_minutes || 0)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch checked={step.active} onCheckedChange={(v) => updateStep(i, { active: v })} />
                    <Button variant="ghost" size="icon" onClick={() => removeStep(i)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2 border-t">
            <Button onClick={save} data-testid="btn-save-seq"><Save className="h-4 w-4 me-2" /> حفظ</Button>
            <Button variant="outline" onClick={cancelEdit}>إلغاء</Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {sequences.length === 0 ? (
            <Card className="p-8 text-center text-foreground/50">لا توجد تسلسلات. أنشئ واحدًا للبدء.</Card>
          ) : sequences.map((s) => {
            const parts = splitTrigger(s.trigger);
            return (
            <Card key={s.id} className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold flex items-center gap-2 flex-wrap">
                  {s.name}
                  {s.active
                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-800">مُفعّل ويعمل</span>
                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">مُعطّل</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-800" data-testid={`seq-funnel-badge-${s.id}`}>
                    {FUNNEL_LABELS[parts.funnel] || parts.funnel}
                  </span>
                </div>
                <div className="text-xs text-foreground/60 mt-1">مُشغِّل: {parts.base} • {s.steps.length} رسالة</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant={s.active ? "outline" : "default"} onClick={() => toggle(s.id)} data-testid={`toggle-seq-${s.id}`}>
                  {s.active ? <><PowerOff className="h-4 w-4 me-1" /> إيقاف</> : <><Power className="h-4 w-4 me-1" /> تفعيل</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => editSequence(s)}>تعديل</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(s.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
